// Pure keyboard-navigation model for the global "Search iYuga" results dropdown
// (EW-1182). Kept out of the SearchBar component so the index math is
// unit-testable and identical for the Users and Wheels tabs. The component owns
// side effects (setState, clicking the highlighted DOM node); this decides ONLY
// what should happen for a given key + current highlight + result count.
//
// Returned action shapes:
//   { type: "highlight", index }  -> move the highlight to `index`
//   { type: "open", index }       -> open (native-click) the result at `index`
//   { type: "close" }             -> close the dropdown
//   null                          -> not a navigation key / no-op (event passes through)

export function searchNavAction(key, currentIndex, listLength) {
  const len =
    Number.isFinite(listLength) && listLength > 0 ? Math.floor(listLength) : 0;
  const cur = Number.isInteger(currentIndex) ? currentIndex : -1;

  switch (key) {
    case "ArrowDown": {
      if (len <= 0) return null;
      // From "nothing highlighted" (-1) -> first row; wraps last -> first.
      const from = cur < 0 ? -1 : cur;
      return { type: "highlight", index: (from + 1) % len };
    }
    case "ArrowUp": {
      if (len <= 0) return null;
      // From "nothing highlighted" -> last row; wraps first -> last.
      const from = cur < 0 ? 0 : cur;
      return { type: "highlight", index: (from - 1 + len) % len };
    }
    case "Enter": {
      if (len <= 0) return null;
      // Open the highlighted row, or the first row when none is highlighted.
      const target = cur >= 0 && cur < len ? cur : 0;
      return { type: "open", index: target };
    }
    case "Escape":
      return { type: "close" };
    default:
      return null;
  }
}

// Is this key the "activate" key for a custom (non-<button>) control that has
// been given role="button" + tabIndex={0}?
//
// MEASURED (web-stg, bundle main.1d538a78.js, 2026-07-28): the clear (×) icon in
// the search field is an <img role="button" tabIndex="0"> carrying an onClick and
// NOTHING else — its React props expose no onKeyDown/onKeyUp/onKeyPress at all.
// Live probe: the icon takes focus (document.activeElement === icon), but Enter
// left the query "a" untouched and Space left it "a" too, while a mouse click on
// the SAME node cleared it to "" (positive control). So it is a focusable-but-
// inert control — the classic G-20 trap: a keyboard user can tab ONTO the clear
// button and then cannot fire it.
//
// The native <button> contract, which role="button" promises to emulate, fires on
// BOTH Enter and Space. Space additionally scrolls the page by default, so the
// caller must preventDefault — see the handler in index.jsx.
export function isActivationKey(key) {
  return key === "Enter" || key === " " || key === "Spacebar";
}

// EW-1182 (Esc-to-close re-fix): decide whether the results dropdown should be
// mounted. Normally the popup shows whenever there is a query, results, or an
// in-flight fetch — but an explicit Escape ("dismissed") must override all of
// those and keep it CLOSED until the user types again. The original render gate
// was `showResults || searchText || loading`; leaving searchText truthy kept the
// dropdown mounted after Escape (only aria-expanded flipped). This isolates the
// gate so the close behavior is unit-testable, not only live-QA-able. Passing a
// falsy `dismissed` reproduces the original truthiness exactly (flag-OFF stays
// byte-identical: `dismissed` is only ever set by the gated Escape handler).
export function searchResultsOpen({ dismissed, showResults, searchText, loading }) {
  if (dismissed) return false;
  // A CLEARED query can never hold the dropdown open. Two measured orphan states
  // both came in through the old `showResults || searchText || loading`:
  //   * clearing the box (the X) left a stale `showResults` from the previous
  //     query, so the dropdown stayed mounted with rows for a query that is no
  //     longer in the input (measured on web-stg 2026-07-24: input "", dropdown
  //     still mounted with 4 rows 5.2s after the clear);
  //   * `fetchData`'s empty-query early-return never cleared `loading`, so a
  //     stuck `loading` held the popup open on a permanent spinner with zero
  //     requests in flight.
  // The popup is a results-for-a-query surface: no query => closed.
  if (!searchText) return false;
  return Boolean(showResults || searchText || loading);
}
